/**
 * Classification of `navigator.mediaDevices.getUserMedia` failures.
 *
 * The pitch recorder used to report EVERY rejection with `captureException`,
 * so a citizen simply declining the camera prompt raised an error-level Sentry
 * alert (prod issues JOURNEY-5X on `/checkup/assessment` and JOURNEY-5W on
 * `/testit`). Declining or dismissing a permission prompt is a CHOICE, not a
 * fault — the widget already handles it correctly with a localized banner and a
 * file-upload fallback. The reporting decision was simply being made before the
 * classification that should inform it.
 *
 * Reads `err.name` rather than testing `instanceof DOMException`: an error that
 * crosses a realm boundary (an iframe, a WebView bridge) fails the `instanceof`
 * while still carrying the spec-mandated `name`.
 */

export type MediaErrorKind = 'denied' | 'no-device' | 'aborted' | 'unknown';

/**
 * `NotReadableError` is deliberately NOT mapped. The camera exists and the user
 * allowed it, but the OS or another application would not hand it over — that is
 * a genuine fault worth paging about, so it falls through to `unknown` and keeps
 * being reported as an exception.
 */
const KIND_BY_NAME: Record<string, MediaErrorKind> = {
  // The user dismissed the prompt or explicitly refused. Both arrive as
  // NotAllowedError ("Permission dismissed" vs "Permission denied").
  NotAllowedError: 'denied',
  // Chrome's pre-spec name for the same condition.
  PermissionDeniedError: 'denied',
  // No camera/microphone attached, or none matching the constraints.
  NotFoundError: 'no-device',
  DevicesNotFoundError: 'no-device',
  OverconstrainedError: 'no-device',
  ConstraintNotSatisfiedError: 'no-device',
  // The page navigated away, or the track was stopped mid-request.
  AbortError: 'aborted',
};

/** Kinds that are expected outcomes rather than defects. */
export function isExpectedMediaError(kind: MediaErrorKind): boolean {
  return kind !== 'unknown';
}

export function classifyMediaError(err: unknown): MediaErrorKind {
  const name = (err as { name?: unknown } | null | undefined)?.name;
  if (typeof name !== 'string') return 'unknown';
  return KIND_BY_NAME[name] ?? 'unknown';
}
